import { useQuery, type QueryFunctionContext } from "@tanstack/react-query";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import { reportingColors } from "features/Library/Common/utils/constants";
import packsDataService from "../services/packsDataService";
import { type CustomerAssignedLicensesResponse, type DepartmentUsage } from "../types/state";
import { type PerformanceFilter } from "features/Library/Common/utils/performanceUtils";

type QueryContextCustomer = QueryFunctionContext<
  [_key: string, packId: number, filter: PerformanceFilter, customerId?: number]
>;

export const getCustomerAssignedLicenses = async ({
  queryKey,
  signal,
}: QueryContextCustomer): Promise<{ raw: CustomerAssignedLicensesResponse; chartData: DataPoint[] }> => {
  const [_key, packId, filter, customerId] = queryKey;
  const { data } = await packsDataService.getCustomerAssignedLicenses({ packId, customerId, ...filter }, signal!);
  return {
    raw: data,
    chartData: transformToChartData(data),
  };
};

export const transformToChartData = (data: CustomerAssignedLicensesResponse): DataPoint[] => {
  const chartData = [
    {
      id: "assigned",
      category: "Issued",
      fillColor: reportingColors.blue,
      value: data.AssignedLicenses,
    },
    {
      id: "remaining",
      category: "Remaining",
      fillColor: reportingColors.yellow,
      value: data.RemainingLicenses,
    },
  ];

  const hasData = chartData.some((item) => item.value > 0);
  return hasData ? chartData : [];
};

export const useCustomerAssignedLicensesQuery = ({
  packId,
  filter,
  customerId,
  enabled = true,
}: {
  packId: number;
  filter: PerformanceFilter;
  customerId?: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryFn: getCustomerAssignedLicenses,
    queryKey: ["customerAssignedLicenses", packId, filter, customerId],
    enabled: !!packId && enabled,
  });
};

export const getCustomerDepartmentUsage = async ({ queryKey, signal }: QueryContextCustomer): Promise<DataPoint[]> => {
  const [_key, packId, filter, customerId] = queryKey;
  const { data } = await packsDataService.getCustomerDepartmentUsage({ packId, customerId, ...filter }, signal!);
  return transformToBarChartData(data);
};

export const transformToBarChartData = (data: DepartmentUsage[]): DataPoint[] => {
  const NO_DEPARTMENT_KEY = "No Department";
  let mappedDepartments: { [key: string]: DataPoint } = {};

  data.forEach((department, i) => {
    if (department.Department === "" || department.Department === null) {
      if (mappedDepartments[NO_DEPARTMENT_KEY]) {
        mappedDepartments[NO_DEPARTMENT_KEY].value += department.ActiveUsersCount;
      } else {
        mappedDepartments[NO_DEPARTMENT_KEY] = {
          id: i.toString(),
          category: NO_DEPARTMENT_KEY,
          value: department.ActiveUsersCount,
          fillColor: reportingColors.blue,
        };
      }
    } else {
      const category =
        department.Department === NO_DEPARTMENT_KEY ? department.Department.toUpperCase() : department.Department;
      mappedDepartments[category] = {
        id: i.toString(),
        category,
        value: department.ActiveUsersCount,
        fillColor: reportingColors.blue,
      };
    }
  });

  return Object.values(mappedDepartments);
};

export const useCustomerDepartmentUsageQuery = ({
  packId,
  filter,
  customerId,
  enabled = true,
}: {
  packId: number;
  filter: PerformanceFilter;
  customerId?: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryFn: getCustomerDepartmentUsage,
    queryKey: ["customerDepartmentUsage", packId, filter, customerId],
    enabled: !!packId && enabled,
  });
};
